<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { AuraProcessingCost } from '@/types';

defineProps<{
    items: AuraProcessingCost[];
    isLoading: boolean;
    formatCost: (value: number) => string;
    formatDate: (value: string) => string;
}>();

const { t, te } = useI18n();

const translateProcessingType = (processingType: string): string => {
    const key = `aura.processingCosts.processingTypes.${processingType}`;
    return te(key) ? t(key) : t('aura.processingCosts.processingTypes.unknown');
};

const translateSourceType = (sourceType: string): string => {
    const key = `aura.processingCosts.sourceTypes.${sourceType}`;
    return te(key) ? t(key) : t('aura.processingCosts.sourceTypes.unknown');
};

const translateStatus = (status: string): string => {
    const key = `aura.processingCosts.statuses.${status}`;
    return te(key) ? t(key) : t('aura.processingCosts.statuses.unknown');
};

const formatProcessingLabel = (item: AuraProcessingCost): string => {
    const processingType = translateProcessingType(item.processingType);

    return item.callId
        ? `${processingType} · ${t('aura.processingCosts.call', { callId: item.callId })}`
        : processingType;
};

const getStatusClass = (status: string): string => {
    const classes: Record<string, string> = {
        completed: 'bg-[#E6F9F5] text-[#159A87]',
        failed: 'bg-[#FDEBEC] text-[#C94B51]',
        skipped: 'bg-[#F1F3F5] text-[#626468]',
        pending: 'bg-[#FEF5DB] text-[#A56D00]',
        processing: 'bg-[#E8F0FE] text-[#3565B5]',
    };

    return classes[status] || classes.skipped;
};
</script>

<template>
    <div
        class="min-h-0 flex-1 overflow-auto rounded-2xl border border-[#E5E7EB] bg-white shadow-sm"
    >
        <table class="min-w-[720px] divide-y divide-[#E5E7EB] text-left text-sm">
            <thead
                class="sticky top-0 bg-[#F8FAFC] text-xs uppercase tracking-wider text-[#626468]"
            >
                <tr>
                    <th class="px-5 py-4">{{ $t('aura.processingCosts.table.processing') }}</th>
                    <th class="px-5 py-4">
                        {{ $t('aura.processingCosts.table.userConversation') }}
                    </th>
                    <th class="px-5 py-4">{{ $t('aura.processingCosts.table.date') }}</th>
                    <th class="px-5 py-4">{{ $t('aura.processingCosts.table.status') }}</th>
                    <th class="px-5 py-4 text-right">
                        {{ $t('aura.processingCosts.table.tokens') }}
                    </th>
                    <th class="px-5 py-4 text-right">
                        {{ $t('aura.processingCosts.table.cost') }}
                    </th>
                </tr>
            </thead>
            <tbody class="divide-y divide-[#F1F3F5]">
                <tr v-for="item in items" :key="item.id" class="hover:bg-[#F8FAFC]">
                    <td class="px-5 py-4">
                        <p class="font-semibold text-[#242B59]">
                            {{ formatProcessingLabel(item) }}
                        </p>
                        <p class="mt-1 text-xs text-[#626468]">
                            {{ translateSourceType(item.sourceType) }}
                        </p>
                    </td>
                    <td class="px-5 py-4">
                        <p class="font-medium text-[#242B59]">
                            {{
                                item.conversation?.userName ||
                                $t('aura.processingCosts.systemProcessing')
                            }}
                        </p>
                        <p class="max-w-xs truncate text-xs text-[#626468]">
                            {{
                                item.conversation?.title ||
                                $t('aura.processingCosts.noConversation')
                            }}
                        </p>
                    </td>
                    <td class="whitespace-nowrap px-5 py-4 text-[#626468]">
                        {{ formatDate(item.startedAt) }}
                    </td>
                    <td class="px-5 py-4">
                        <span
                            class="rounded-full px-2 py-1 text-xs font-semibold"
                            :class="getStatusClass(item.status)"
                        >
                            {{ translateStatus(item.status) }}
                        </span>
                    </td>
                    <td class="px-5 py-4 text-right text-[#626468]">{{ item.tokensUsed || 0 }}</td>
                    <td class="px-5 py-4 text-right font-semibold text-[#242B59]">
                        {{ formatCost(item.costUsd) }}
                    </td>
                </tr>
                <tr v-if="!isLoading && !items.length">
                    <td colspan="6" class="px-5 py-12 text-center text-sm text-[#626468]">
                        {{ $t('aura.processingCosts.empty') }}
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>
